import React, { useEffect, useMemo, useState } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import dayjs from "dayjs";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tab,
  Tabs,
  Avatar,
  Alert,
  Divider,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import {
  Add,
  Close,
  CheckCircle,
  Cancel,
  Pending,
  CalendarMonth,
  AccessTime,
  EventNote,
  Visibility,
  AttachFile,
  CloudUpload,
  Edit,
  WarningAmber,
  Block,
  DoNotDisturb,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import leaveService from "../../services/leaveService";
import api from "../../services/api";
import PrimaryButton from "../../components/ui/PrimaryButton";

// Expand holiday ranges to individual date strings (YYYY-MM-DD)
const getHolidayDateSet = (holidays) => {
  const set = new Set();
  if (!Array.isArray(holidays)) return set;
  holidays.forEach((h) => {
    const start = h.startDate ? String(h.startDate).slice(0, 10) : null;
    const end = h.endDate ? String(h.endDate).slice(0, 10) : start;
    if (!start) return;
    const startD = new Date(start);
    const endD = new Date(end);
    let d = new Date(startD);
    while (d <= endD) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      set.add(`${y}-${m}-${day}`);
      d.setDate(d.getDate() + 1);
    }
  });
  return set;
};

// Check if date should be disabled (Sunday or holiday)
const createShouldDisableDate = (holidayDateSet) => (date) => {
  if (!date) return false;
  const d = date?.toDate ? date.toDate() : date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return false;
  if (d.getDay() === 0) return true; // Sunday
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const dateStr = `${y}-${m}-${day}`;
  return holidayDateSet.has(dateStr);
};

const DEFAULT_LEAVE_TYPE_OPTIONS = [
  {
    id: "default-casual-leave",
    value: "Casual Leave",
    label: "Casual Leave",
    color: "#2196f3",
    requiredDocument: false,
    manageId: "default-casual-leave",
  },
  {
    id: "default-sick-leave",
    value: "Sick Leave",
    label: "Sick Leave",
    color: "#f44336",
    requiredDocument: false,
    manageId: "default-sick-leave",
  },
  {
    id: "default-earned-leave",
    value: "Earned Leave",
    label: "Earned Leave",
    color: "#4caf50",
    requiredDocument: false,
    manageId: "default-earned-leave",
  },
  {
    id: "default-medical-leave",
    value: "Medical Leave",
    label: "Medical Leave",
    color: "#ff9800",
    requiredDocument: false,
    manageId: "default-medical-leave",
  },
  {
    id: "default-maternity-leave",
    value: "Maternity Leave",
    label: "Maternity Leave",
    color: "#e91e63",
    requiredDocument: false,
    manageId: "default-maternity-leave",
  },
  {
    id: "default-paternity-leave",
    value: "Paternity Leave",
    label: "Paternity Leave",
    color: "#9c27b0",
    requiredDocument: false,
    manageId: "default-paternity-leave",
  },
  {
    id: "default-compensatory-off",
    value: "Compensatory Off",
    label: "Compensatory Off",
    color: "#00bcd4",
    requiredDocument: false,
    manageId: "default-compensatory-off",
  },
  {
    id: "default-on-duty",
    value: "On Duty",
    label: "On Duty",
    color: "#607d8b",
    requiredDocument: false,
    manageId: "default-on-duty",
  },
];

const buildDefaultLeaveTypeOptions = () =>
  DEFAULT_LEAVE_TYPE_OPTIONS.map((option) => ({ ...option }));

const LEAVE_CARD_COLORS = [
  "#667eea",
  "#26c6da",
  "#ab47bc",
  "#ff9800",
  "#4caf50",
  "#ef5350",
  "#5c6bc0",
];

const LEAVE_CARD_ICON_COMPONENTS = [
  AccessTime,
  CalendarMonth,
  CheckCircle,
  Pending,
  EventNote,
  Cancel,
];

const toFiniteNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const formatCount = (value) => {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return "-";
  return new Intl.NumberFormat().format(numeric);
};

const extractLeaveSummary = (payload = {}) => {
  const candidates = [
    payload?.leaveSummary,
    payload?.data?.leaveSummary,
    payload?.data?.data?.leaveSummary,
    payload?.data?.data?.data?.leaveSummary,
  ];

  for (const candidate of candidates) {
    if (candidate && typeof candidate === "object") {
      return candidate;
    }
  }

  return null;
};

const buildOverallSummaryCards = (summary) => {
  if (!summary) return [];

  const cards = [];

  const totalLeaves = toFiniteNumber(summary.totalLeaves);
  if (totalLeaves !== null) {
    cards.push({
      key: "total-leaves",
      label: "Total Leaves Allowed",
      subtitle: "All leave types combined",
      value: formatCount(totalLeaves),
      iconComponent: EventNote,
      color: "#667eea",
    });
  }

  const totalApprovedLeaves = toFiniteNumber(summary.totalApprovedLeaves);
  if (totalApprovedLeaves !== null) {
    cards.push({
      key: "total-approved",
      label: "Total Days Approved",
      subtitle: "Approved leave requests",
      value: formatCount(totalApprovedLeaves),
      iconComponent: CheckCircle,
      color: "#4caf50",
    });
  }

  if (totalLeaves !== null && totalApprovedLeaves !== null) {
    const remaining = Math.max(totalLeaves - totalApprovedLeaves, 0);
    cards.push({
      key: "total-remaining",
      label: "Remaining Days Leaves",
      subtitle: "Still available to apply",
      value: formatCount(remaining),
      iconComponent: AccessTime,
      color: "#26c6da",
    });

    const extra = totalApprovedLeaves - totalLeaves;
    if (extra > 0) {
      cards.push({
        key: "extra-leaves",
        label: "Extra Days Leaves Used",
        subtitle: "Approved beyond allocation",
        value: formatCount(extra),
        iconComponent: Cancel,
        color: "#ef5350",
      });
    }
  }

  return cards;
};

const buildTypeWiseSummaryCards = (summary) => {
  const items = Array.isArray(summary?.typeWiseCounts)
    ? summary.typeWiseCounts
    : [];

  return items
    .map((item, index) => {
      const totalAllowed = toFiniteNumber(item?.totalAllowed);
      const approvedCount = toFiniteNumber(item?.approvedCount);
      const remaining =
        totalAllowed !== null && approvedCount !== null
          ? Math.max(totalAllowed - approvedCount, 0)
          : null;

      const primaryValue = formatCount(remaining ?? totalAllowed ?? approvedCount);

      if (primaryValue === "-") return null;

      const displayValue = (() => {
        if (remaining !== null && totalAllowed !== null) {
          return `${formatCount(remaining)} of ${formatCount(totalAllowed)} remaining`;
        }
        if (totalAllowed !== null) {
          return `${formatCount(totalAllowed)} total`;
        }
        if (approvedCount !== null) {
          return `${formatCount(approvedCount)} approved`;
        }
        return primaryValue;
      })();

      const subtitleParts = [];
      if (approvedCount !== null) {
        subtitleParts.push(`${formatCount(approvedCount)} approved`);
      }

      const iconComponent =
        LEAVE_CARD_ICON_COMPONENTS[index % LEAVE_CARD_ICON_COMPONENTS.length] || EventNote;
      const color = LEAVE_CARD_COLORS[index % LEAVE_CARD_COLORS.length];

      return {
        key: `leave-type-${item?.leaveTypeId ?? index}`,
        label: item?.leaveTypeName || item?.name || `Leave Type ${index + 1}`,
        subtitle: subtitleParts.join(" • ") || undefined,
        value: displayValue,
        iconComponent,
        color,
      };
    })
    .filter(Boolean);
};

const getStatGridConfig = (length) => {
  if (length <= 1) {
    return { sm: 12, md: 6, lg: 4 };
  }

  if (length === 2) {
    return { sm: 6, md: 6, lg: 4 };
  }

  if (length === 3) {
    return { sm: 6, md: 4, lg: 4 };
  }

  return { sm: 6, md: 4, lg: 3 };
};

const StaffLeaves = () => {
  const { user } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState(0);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [formData, setFormData] = useState({
    type: "",
    startDate: null,
    endDate: null,
    leaveDurationType: "FULL_DAY",
    halfDayPeriod: "FIRST_HALF",
    reason: "",
    attachment: null,
  });
  const [leaves, setLeaves] = useState([]);
  const [loadingLeaves, setLoadingLeaves] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [refreshKey, setRefreshKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [cancelingId, setCancelingId] = useState(null);
  const [editingLeave, setEditingLeave] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [existingDocument, setExistingDocument] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [leaveToCancel, setLeaveToCancel] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveTypesLoading, setLeaveTypesLoading] = useState(false);
  const [leaveStats, setLeaveStats] = useState([]);
  const [leaveStatsLoading, setLeaveStatsLoading] = useState(false);
  const [leaveTypeStats, setLeaveTypeStats] = useState([]);
  const [leaveTypeLimits, setLeaveTypeLimits] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);

  const isEditing = Boolean(editingLeave?.id);
  const holidayDateSet = useMemo(() => getHolidayDateSet(holidays), [holidays]);
  const shouldDisableDate = useMemo(
    () => createShouldDisableDate(holidayDateSet),
    [holidayDateSet]
  );
  
  const resetForm = () => {
    setFormData({
      type: "",
      startDate: null,
      endDate: null,
      reason: "",
      attachment: null,
    });
    setExistingDocument(null);
    setFormErrors({});
  };

  const closeSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const openApplyDialog = () => {
    setEditingLeave(null);
    resetForm();
    setApplyDialogOpen(true);
  };

  const closeApplyDialog = () => {
    setApplyDialogOpen(false);
    setEditingLeave(null);
    resetForm();
    setDialogLoading(false);
  };

  const formatDate = (date) => {
    if (!date) return null;
    if (typeof date === "string") return date;
    if (typeof date?.format === "function") {
      return date.format("YYYY-MM-DD");
    }
    return new Date(date).toISOString().split("T")[0];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "pending":
        return "warning";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle fontSize="small" />;
      case "pending":
        return <Pending fontSize="small" />;
      case "rejected":
        return <Cancel fontSize="small" />;
      default:
        return <AccessTime fontSize="small" />;
    }
  };

  const filteredLeaves = leaves.filter((leave) => {
    if (activeTab === 0) return true;
    if (activeTab === 1) return leave.status === "pending";
    if (activeTab === 2) return leave.status === "approved";
    if (activeTab === 3) return leave.status === "rejected";
    return true;
  });

  const formatLeaveType = (type) =>
    type?.trim().toLowerCase().replace(/\s+/g, "_") || "";

  const formatDisplayDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return dateString.split("T")[0] || dateString;
    }
    return date.toISOString().split("T")[0];
  };

  const mapLeaveTypeLabel = (type, typesList = leaveTypes) => {
    if (!type) return "Unknown";
    const normalizedType = type.toLowerCase?.() || String(type).toLowerCase();
    const match = typesList.find(
      (leaveType) =>
        formatLeaveType(leaveType.value).toLowerCase() === normalizedType
    );
    return match?.label || type;
  };

  const transformLeaves = (records = [], typesList = leaveTypes) =>
    records.map((leave) => ({
      id: leave.id,
      type: mapLeaveTypeLabel(leave.leaveType, typesList),
      startDate: leave.leaveFrom,
      endDate: leave.leaveTo,
      days: leave.duration,
      reason: leave.notes || "-",
      status: leave.status,
      appliedOn: formatDisplayDate(leave.createdAt),
      approvedBy: leave.approvedBy || null,
      approvedOn: leave.approvedOn || null,
      remarks: leave.remarks,
      attachment: leave.document,
      document: leave.document,
      raw: leave,
    }));

  const toDateValue = (value) => (value ? new Date(value) : null);
  const fetchLeaveTypes = async () => {
    setLeaveTypesLoading(true);
    try {
      const response = await leaveService.getLeaveManagementTypes();
      const payload = response?.data;
      const rawTypes = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.leaveTypes)
        ? payload.leaveTypes
        : Array.isArray(payload)
        ? payload
        : [];

      const options = rawTypes
        .map((record) => {
          const name =
            record?.leaveTypeName ||
            record?.name ||
            record?.label ||
            record?.type ||
            record?.title ||
            "";
          const code =
            record?.code ||
            record?.leaveTypeCode ||
            record?.slug ||
            record?.identifier ||
            "";
          const label =
            name ||
            (typeof code === "string" && code
              ? code.replace(/[_-]+/g, " ").trim()
              : "");

          if (!label) return null;

          return {
            id:
              record?.id ??
              record?._id ??
              record?.leaveTypeId ??
              record?.uuid ??
              label,
            value: label,
            label,
            color: record?.color || record?.colour || "#667eea",
            requiredDocument: Boolean(
              record?.requiredDocument ??
                record?.documentRequired ??
                record?.requiresDocument ??
                false
            ),
            advanceNoticeDays:
              record?.advanceNoticeDays ??
              record?.advanceNoticeDay ??
              record?.advanceNotice ??
              0,
            manageId:
              record?.manageId ??
              record?.leaveManageId ??
              record?.leaveTypeManageId ??
              record?.id ??
              record?._id ??
              record?.leaveTypeId ??
              record?.uuid ??
              label,
          };
        })
        .filter(Boolean);

      setLeaveTypes(options);
    } catch (error) {
      console.error("Failed to fetch leave types", error);
      setLeaveTypes(buildDefaultLeaveTypeOptions());
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to load leave types.";
      setSnackbar({ open: true, severity: "error", message });
    } finally {
      setLeaveTypesLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  useEffect(() => {
    if (!applyDialogOpen) return;
    let cancelled = false;
    setHolidaysLoading(true);
    api
      .get("/academic-info/holidays/all", {
        params: { page: 1, limit: 100, sort: "startDate", order: "ASC" },
      })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data ?? res?.data ?? {};
        const list = data.holidays ?? data ?? [];
        setHolidays(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setHolidays([]);
      })
      .finally(() => {
        if (!cancelled) setHolidaysLoading(false);
      });
    return () => { cancelled = true; };
  }, [applyDialogOpen]);

  const fetchLeaves = async () => {
      setLoadingLeaves(true);
      setLeaveStatsLoading(true);
      try {
        const response = await leaveService.getUserOwnLeaves({
          page: paginationModel.page + 1,
          limit: paginationModel.pageSize,
          search: "",
        });

        const payload = response?.data || {};
        const remoteLeaves = payload.data || payload.leaves || [];
        setLeaves(transformLeaves(remoteLeaves));

        const summary = extractLeaveSummary(payload);
        const overallCards = buildOverallSummaryCards(summary);
        const typeCards = buildTypeWiseSummaryCards(summary);
        const typeWiseCounts = Array.isArray(summary?.typeWiseCounts) ? summary.typeWiseCounts : [];
        setLeaveStats(overallCards);
        setLeaveTypeStats(typeCards);
        setLeaveTypeLimits(typeWiseCounts);

        if (payload.pagination) {
          setPaginationInfo(payload.pagination);
        } else {
          setPaginationInfo((prev) => ({
            ...prev,
            total: remoteLeaves.length,
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
            totalPages: 1,
          }));
        }
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Unable to fetch leave applications.";
        setSnackbar({ open: true, severity: "error", message });
        setLeaveStats([]);
        setLeaveTypeStats([]);
        setLeaveTypeLimits([]);
      } finally {
        setLoadingLeaves(false);
        setLeaveStatsLoading(false);
      }
    };
    
  useEffect(() => {
    fetchLeaves();
  }, [paginationModel.page, paginationModel.pageSize, refreshKey]);

  const selectedLeaveType = useMemo(() => {
    if (!formData.type) return null;
    const directMatch = leaveTypes.find((option) => option.value === formData.type);
    if (directMatch) return directMatch;
    const normalizedSelected = formatLeaveType(formData.type);
    return leaveTypes.find(
      (option) => formatLeaveType(option.value) === normalizedSelected
    );
  }, [formData.type, leaveTypes]);

  const selectedLeaveTypeLimit = useMemo(() => {
    if (!formData.type || !leaveTypeLimits.length) return null;
    const name = (formData.type || "").trim();
    const match = leaveTypeLimits.find(
      (item) =>
        (item?.leaveTypeName || "").trim().toLowerCase() === name.toLowerCase()
    );
    if (!match) return null;
    const totalAllowed = toFiniteNumber(match?.totalAllowed);
    const approvedCount = toFiniteNumber(match?.approvedCount);
    const remaining =
      totalAllowed !== null && approvedCount !== null
        ? Math.max(totalAllowed - approvedCount, 0)
        : totalAllowed;
    return {
      totalAllowed,
      approvedCount,
      remaining,
      leaveTypeName: match?.leaveTypeName,
    };
  }, [formData.type, leaveTypeLimits]);

  const requestedLeaveDays =
    formData.leaveDurationType === "HAL_DAY" && formData.startDate
      ? 0.5
      : formData.startDate && formData.endDate
        ? (() => {
            const start = new Date(formData.startDate);
            const end = new Date(formData.endDate);
            const diffTime = Math.abs(end - start);
            return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
          })()
        : 0;
  const exceedsTotalAllowed =
    requestedLeaveDays > 0 &&
    selectedLeaveTypeLimit != null &&
    selectedLeaveTypeLimit.remaining != null &&
    requestedLeaveDays > selectedLeaveTypeLimit.remaining;

  const isAttachmentRequired = Boolean(selectedLeaveType?.requiredDocument);
  const hasAttachment = Boolean(formData.attachment || existingDocument);
  const isMissingRequiredAttachment = isAttachmentRequired && !hasAttachment;

  const handleSubmit = async () => {
    if (submitting) return;

    if (exceedsTotalAllowed) {
      setSnackbar({
        open: true,
        severity: "error",
        message: `Leave duration exceeds the allowed limit. You can apply for at most ${selectedLeaveTypeLimit?.remaining ?? selectedLeaveTypeLimit?.totalAllowed} day(s) for ${selectedLeaveTypeLimit?.leaveTypeName || formData.type}.`,
      });
      return;
    }

    // Enforce advance notice rule (fast feedback; backend also validates)
    const noticeDays = Math.max(
      0,
      parseInt(selectedLeaveType?.advanceNoticeDays, 10) || 0
    );
    if (noticeDays > 0 && formData.startDate) {
      const diffDays = dayjs(formData.startDate)
        .startOf("day")
        .diff(dayjs().startOf("day"), "day");
      if (diffDays < noticeDays) {
        setSnackbar({
          open: true,
          severity: "error",
          message: `You must apply at least ${noticeDays} day(s) in advance for this leave type.`,
        });
        return;
      }
    }

    if (isMissingRequiredAttachment) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please upload the required document for this leave type.",
      });
      return;
    }

    const leaveManageId =
      selectedLeaveType?.manageId ||
      selectedLeaveType?.id ||
      (formData.type ? formatLeaveType(formData.type) : undefined);

    const isHalfDay = formData.leaveDurationType === "HALF_DAY";
    const leaveFrom = formatDate(formData.startDate);
    const leaveTo = isHalfDay ? leaveFrom : formatDate(formData.endDate);

    const payload = {
      leaveFrom,
      leaveTo,
      leaveManageId,
      notes: formData.reason?.trim(),
      leaveDurationType: formData.leaveDurationType || "FULL_DAY",
      ...(isHalfDay ? { halfDayPeriod: formData.halfDayPeriod || "FIRST_HALF" } : {}),
    };

    let requestBody;
    let config = {};

    if (formData.attachment instanceof File) {
      const formPayload = new FormData();

      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formPayload.append(key, String(value));
        }
      });

      if (isEditing) {
        formPayload.append("leaveId", String(editingLeave.id));
      }

      formPayload.append("document", formData.attachment, formData.attachment.name);

      requestBody = formPayload;
      config = {};
    } else {
      if (isEditing) {
        payload.leaveId = editingLeave.id;
      }
      requestBody = payload;
    }

    setSubmitting(true);

    try {
      let response;

      if (isEditing) {
        response = await leaveService.updateLeave(requestBody, config);
      } else {
        response = await leaveService.createLeave(requestBody, config);
      }

      const isSuccess =
        response?.status === 200 ||
        response?.status === 201 ||
        response?.data?.status === 200 ||
        response?.data?.status === 201 ||
        (response?.data && !response?.data?.error);

      if (!isSuccess) {
        throw new Error(response?.data?.message || "Submit failed");
      }

      const successMessage =
        response?.data?.message ||
        (isEditing
          ? "Leave request updated successfully."
          : "Leave request created successfully.");

      setSnackbar({
        open: true,
        severity: "success",
        message: successMessage,
      });

      closeApplyDialog();
      setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to submit leave application.";

      setSnackbar({ open: true, severity: "error", message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewLeave = (leave) => {
    setSelectedLeave(leave);
    setViewDialogOpen(true);
  };

  const handleEditLeave = (row) => {
    if (dialogLoading) return;
    setDialogLoading(true);
    setApplyDialogOpen(true);
    resetForm();
    const fetchDetails = async () => {
      try {
        const response = await leaveService.getLeaveDetails(row.id);
        const detailedLeave = response?.data?.data || response?.data;
        if (!detailedLeave) {
          throw new Error("Unable to load leave details.");
        }
        setEditingLeave(detailedLeave);
        const typeLabel = mapLeaveTypeLabel(detailedLeave.leaveType);
        const typeOption =
          leaveTypes.find((t) => t.label === typeLabel)?.value ||
          leaveTypes.find(
            (t) =>
              formatLeaveType(t.value) ===
              formatLeaveType(detailedLeave.leaveType)
          )?.value ||
          typeLabel;
        setFormData({
          type: typeOption,
          startDate: toDateValue(detailedLeave.leaveFrom),
          endDate: toDateValue(detailedLeave.leaveTo),
          reason: detailedLeave.notes || "",
          attachment: null,
        });
        setExistingDocument(detailedLeave.document || null);
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to load leave details.";
        setSnackbar({ open: true, severity: "error", message });
        closeApplyDialog();
      } finally {
        setDialogLoading(false);
      }
    };

    fetchDetails();
  };

  const handleCancelLeave = async (leaveId) => {
    if (!leaveId) return;
    setCancelingId(leaveId);
    try {
      await leaveService.cancelLeave(leaveId);
      setSnackbar({
        open: true,
        severity: "success",
        message: "Leave request cancelled successfully.",
      });
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to cancel leave request.";
      setSnackbar({ open: true, severity: "error", message });
    } finally {
      setCancelingId(null);
      setLeaveToCancel(null);
      setCancelDialogOpen(false);
    }
  };

  const openCancelDialog = (row) => {
    setLeaveToCancel(row);
    setCancelDialogOpen(true);
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setLeaveToCancel(null);
  };

  const calculateDays = () => {
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }
    return 0;
  };

  const columns = [
    {
      field: "type",
      headerName: "Leave Type",
      width: 160,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor:
              leaveTypes.find((t) => t.value === params.value)?.color + "20",
            color: leaveTypes.find((t) => t.value === params.value)?.color,
            fontWeight: 500,
          }}
        />
      ),
    },
    {
      field: "startDate",
      headerName: "From",
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <CalendarMonth sx={{ fontSize: 16, color: "text.secondary" }} />
          {params.value}
        </Box>
      ),
    },
    { field: "endDate", headerName: "To", width: 120 },
    {
      field: "days",
      headerName: "Days",
      width: 80,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" />
      ),
    },
    {
      field: "reason",
      headerName: "Reason",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={getStatusColor(params.value)}
          icon={getStatusIcon(params.value)}
          sx={{ textTransform: "capitalize" }}
        />
      ),
    },
    {
      field: "document",
      headerName: "Document",
      width: 140,
      renderCell: (params) =>
        params.value ? (
          <Typography
            component="a"
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: "primary.main",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
              cursor: "pointer",
            }}
          >
            Document
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
    {
      field: "cancel",
      headerName: "Cancelled",
      width: 160, // Increased width to prevent button cutoff
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const isPending = params.row.status === "pending";
        const isCancelling = cancelingId === params.row.id;

        // Show cancelled status
        const cancelledLabel = isPending ? "No" : "Yes";

        return (
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: 1,
            width: "100%",
            justifyContent: "space-between" // Better spacing
          }}>
            <Chip
              label={cancelledLabel}
              size="small"
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: isPending ? "#ff9800" : "#4caf50", // Orange for No, Green for Yes
                color: isPending ? "#ff9800" : "#4caf50",
                bgcolor: isPending ? "#fff3e0" : "#e8f5e8", // Light orange for No, Light green for Yes
                fontWeight: 500,
                minWidth: 45,
              }}
            />

            {/* Cancel action for pending requests */}
            {isPending ? (
              <Button
                variant="outlined"
                size="small"
                disabled={isCancelling}
                onClick={() => openCancelDialog(params.row)}
                startIcon={
                  isCancelling ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <Cancel sx={{ fontSize: 16 }} />
                  )
                }
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 500,
                  borderColor: "grey.400",
                  color: "grey.700",
                  fontSize: "0.75rem", // Smaller font to fit better
                  padding: "4px 8px", // Smaller padding
                  minWidth: "auto", // Allow button to shrink
                  "&:hover": {
                    borderColor: "grey.600",
                    bgcolor: "grey.50",
                    color: "grey.900",
                  },
                  "&:disabled": {
                    borderColor: "grey.300",
                    color: "grey.400",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {isCancelling ? "Cancelling..." : "Cancel"}
              </Button>
            ) : null}
          </Box>
        );
      },
    },
    { field: "appliedOn", headerName: "Applied On", width: 120 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleViewLeave(params.row)}
            // disabled={params.row.status !== "pending"}
          >
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleEditLeave(params.row)}
            disabled={params.row.status !== "pending"}
          >
            <Edit fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const overallStatGridConfig = useMemo(
    () => getStatGridConfig(leaveStats.length),
    [leaveStats.length]
  );

  const typeStatGridConfig = useMemo(
    () => getStatGridConfig(leaveTypeStats.length),
    [leaveTypeStats.length]
  );

  const leaveStatusCards = useMemo(() => {
    const totalApplied =
      toFiniteNumber(paginationInfo.total) ?? toFiniteNumber(leaves.length) ?? 0;

    const pendingCount = leaves.filter((leave) => leave.status === "pending").length;
    const approvedCount = leaves.filter((leave) => leave.status === "approved").length;
    const rejectedCount = leaves.filter((leave) => leave.status === "rejected").length;

    return [
      {
        key: "total-applied",
        label: "Total Applied",
        value: formatCount(totalApplied),
        iconComponent: EventNote,
        color: "#667eea",
      },
      {
        key: "pending",
        label: "Pending",
        value: formatCount(pendingCount),
        iconComponent: Pending,
        color: "#ff9800",
      },
      {
        key: "approved",
        label: "Approved",
        value: formatCount(approvedCount),
        iconComponent: CheckCircle,
        color: "#4caf50",
      },
      {
        key: "rejected",
        label: "Rejected",
        value: formatCount(rejectedCount),
        iconComponent: Cancel,
        color: "#ef5350",
      },
    ];
  }, [leaves, paginationInfo.total]);

  const leaveStatusGridConfig = useMemo(
    () => getStatGridConfig(leaveStatusCards.length),
    [leaveStatusCards.length]
  );

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Apply Leave
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your leave applications and track status
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openApplyDialog}
        >
          Apply Leave
        </Button>
      </Box>

      {/* Leave Statistics */}
      {leaveStatsLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          {leaveStats.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Leave Statistics
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {leaveStats.map((stat) => {
                  const Icon = stat.iconComponent || EventNote;
                  const avatarBg = stat.color ? `${stat.color}20` : "rgba(0,0,0,0.08)";
                  const avatarColor = stat.color || "primary.main";

                  return (
                    <Grid
                      item
                      xs={12}
                      sm={overallStatGridConfig.sm}
                      md={overallStatGridConfig.md}
                      lg={overallStatGridConfig.lg}
                      key={stat.key}
                    >
                      <Card>
                        <CardContent
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            py: 2,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: avatarBg,
                              color: avatarColor,
                              width: 48,
                              height: 48,
                            }}
                          >
                            <Icon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography
                              variant="h6"
                              fontWeight={700}
                              sx={{ mb: 0.5 }}
                            >
                              {stat.label}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              {stat.value}
                            </Typography>
                            {stat.subtitle ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.5 }}
                              >
                                {stat.subtitle}
                              </Typography>
                            ) : null}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}

          {leaveTypeStats.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Leave Type Breakdown
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {leaveTypeStats.map((stat) => {
                  const Icon = stat.iconComponent || EventNote;
                  const avatarBg = stat.color ? `${stat.color}20` : "rgba(0,0,0,0.08)";
                  const avatarColor = stat.color || "primary.main";

                  return (
                    <Grid
                      item
                      xs={12}
                      sm={typeStatGridConfig.sm}
                      md={typeStatGridConfig.md}
                      lg={typeStatGridConfig.lg}
                      key={stat.key}
                    >
                      <Card>
                        <CardContent
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            py: 2,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: avatarBg,
                              color: avatarColor,
                              width: 48,
                              height: 48,
                            }}
                          >
                            <Icon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="h5" fontWeight={700}>
                              {stat.value}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              {stat.label}
                            </Typography>
                            {stat.subtitle ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.5 }}
                              >
                                {stat.subtitle}
                              </Typography>
                            ) : null}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}
        </>
      )}

      {leaveStatusCards.length > 0 && (
        <>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Leave Status Summary
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {leaveStatusCards.map((card) => {
              const Icon = card.iconComponent || EventNote;
              const avatarBg = card.color ? `${card.color}20` : "rgba(0,0,0,0.08)";
              const avatarColor = card.color || "primary.main";

              return (
                <Grid
                  item
                  xs={12}
                  sm={leaveStatusGridConfig.sm}
                  md={leaveStatusGridConfig.md}
                  lg={leaveStatusGridConfig.lg}
                  key={card.key}
                >
                  <Card>
                    <CardContent
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        py: 2,
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: avatarBg,
                          color: avatarColor,
                          width: 48,
                          height: 48,
                        }}
                      >
                        <Icon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" fontWeight={700}>
                          {card.value}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          {card.label}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* Main Card */}
      <Card>
        <CardContent>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ mb: 3 }}
          >
            <Tab label={`All (${leaves.length})`} />
            <Tab
              label={`Pending (${
                leaves.filter((l) => l.status === "pending").length
              })`}
            />
            <Tab
              label={`Approved (${
                leaves.filter((l) => l.status === "approved").length
              })`}
            />
            <Tab
              label={`Rejected (${
                leaves.filter((l) => l.status === "rejected").length
              })`}
            />
          </Tabs>

          <DataGrid
            rows={filteredLeaves}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            loading={loadingLeaves}
            paginationMode="server"
            rowCount={paginationInfo.total}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            sx={{
              border: "none",
              "& .MuiDataGrid-cell": { borderBottom: "1px solid #f0f0f0" },
            }}
          />
        </CardContent>
      </Card>

      {/* Apply Leave Dialog */}
      <Dialog
        open={applyDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; closeApplyDialog(); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
         
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <EventNote />
              <Typography variant="h6" fontWeight={600}>
                {isEditing ? "Edit Leave Request" : "Apply for Leave"}
              </Typography>
            </Box>
            <IconButton
              onClick={closeApplyDialog}
              sx={{ color: "white" }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {dialogLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 200,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Leave Type *</InputLabel>
                  <Select
                    value={formData.type}
                    label="Leave Type *"
                    onChange={(e) =>
                      setFormData({ ...formData, type: e.target.value })
                    }
                    disabled={leaveTypesLoading || !leaveTypes.length}
                  >
                    {leaveTypesLoading ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : leaveTypes.length ? (
                      leaveTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <Box
                            sx={{ display: "flex", alignItems: "center", gap: 1 }}
                          >
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                bgcolor: type.color,
                              }}
                            />
                            {type.label}
                          </Box>
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No leave types available</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Duration *</InputLabel>
                  <Select
                    value={formData.leaveDurationType}
                    label="Duration *"
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        leaveDurationType: v,
                        endDate: v === "HALF_DAY" ? prev.startDate : prev.endDate,
                      }));
                    }}
                  >
                    <MenuItem value="FULL_DAY">Full day</MenuItem>
                    <MenuItem value="HALF_DAY">Half day</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {formData.leaveDurationType === "HALF_DAY" && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Half *</InputLabel>
                    <Select
                      value={formData.halfDayPeriod}
                      label="Half *"
                      onChange={(e) => setFormData({ ...formData, halfDayPeriod: e.target.value })}
                    >
                      <MenuItem value="FIRST_HALF">First half</MenuItem>
                      <MenuItem value="SECOND_HALF">Second half</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={formData.leaveDurationType === "HALF_DAY" ? 12 : 6}>
                <DatePicker
                  label={formData.leaveDurationType === "HALF_DAY" ? "Date *" : "Start Date *"}
                  value={formData.startDate}
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      startDate: date,
                      endDate: prev.leaveDurationType === "HALF_DAY" ? date : prev.endDate,
                    }))
                  }
                  slotProps={{ textField: { fullWidth: true } }}
                  minDate={new Date()}
                  shouldDisableDate={shouldDisableDate}
                />
              </Grid>
              {formData.leaveDurationType !== "HALF_DAY" && (
                <Grid item xs={6}>
                  <DatePicker
                    label="End Date *"
                    value={formData.endDate}
                    onChange={(date) => setFormData({ ...formData, endDate: date })}
                    slotProps={{ textField: { fullWidth: true } }}
                    minDate={formData.startDate || new Date()}
                    shouldDisableDate={shouldDisableDate}
                  />
                </Grid>
              )}
              {calculateDays() > 0 && (
                <Grid item xs={12}>
                  {exceedsTotalAllowed ? (
                    <Alert severity="error" icon={<WarningAmber />}>
                      You cannot apply for <strong>{requestedLeaveDays} day(s)</strong>.{" "}
                      {selectedLeaveTypeLimit?.leaveTypeName} allows a maximum of{" "}
                      <strong>{selectedLeaveTypeLimit?.remaining ?? selectedLeaveTypeLimit?.totalAllowed} day(s)</strong>
                      {selectedLeaveTypeLimit?.approvedCount != null && selectedLeaveTypeLimit.approvedCount > 0 ? (
                        <> ({formatCount(selectedLeaveTypeLimit.remaining)} of {formatCount(selectedLeaveTypeLimit.totalAllowed)} already used)</>
                      ) : null}
                      . Please reduce the leave duration.
                    </Alert>
                  ) : (
                    <Alert severity="info" icon={<CalendarMonth />}>
                      Total Leave Duration:{" "}
                      <strong>{calculateDays()} day(s)</strong>
                      {selectedLeaveTypeLimit?.remaining != null && (
                        <> · <strong>{formatCount(selectedLeaveTypeLimit.remaining)}</strong> of {formatCount(selectedLeaveTypeLimit.totalAllowed)} days remaining for this type</>
                      )}
                    </Alert>
                  )}
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reason *"
                  multiline
                  rows={4}
                  value={formData.reason}
                  onChange={(e) =>
                    setFormData({ ...formData, reason: e.target.value })
                  }
                  placeholder="Please provide a detailed reason for your leave request..."
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {`Attachment ${
                    selectedLeaveType?.requiredDocument ? "(Required)" : "(Optional)"
                  }`}
                </Typography>
                <Box
                  component="label"
                  sx={{
                    border: "2px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        attachment: e.target.files?.[0] || null,
                      })
                    }
                  />
                  <CloudUpload
                    sx={{ fontSize: 40, color: "text.secondary", mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {formData.attachment
                      ? formData.attachment.name
                      : "Upload supporting document (PDF, JPG, PNG)"}
                  </Typography>
                </Box>
                {selectedLeaveType?.requiredDocument && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 1 }}
                  >
                    A supporting document is mandatory for this leave type.
                  </Typography>
                )}
                {formErrors.attachment && (
                  <Typography
                    variant="caption"
                    color="error.main"
                    sx={{ display: "block", mt: selectedLeaveType?.requiredDocument ? 0.5 : 1 }}
                  >
                    {formErrors.attachment}
                  </Typography>
                )}
                {existingDocument && !formData.attachment && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Current: File 1
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => window.open(existingDocument, "_blank")}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setExistingDocument(null)}
                    >
                      Remove
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={closeApplyDialog}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              dialogLoading ||
              submitting ||
              !formData.type ||
              !formData.startDate ||
              !formData.endDate ||
              !formData.reason ||
              isMissingRequiredAttachment ||
              exceedsTotalAllowed
            }
          >
            {submitting ? (isEditing ? "Updating..." : "Submitting...") : isEditing ? "Update " : "Submit "}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; closeCancelDialog(); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle

        >
          <WarningAmber sx={{ color: "white" }} />
          Cancel Leave?
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeCancelDialog} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleCancelLeave(leaveToCancel?.id)}
            disabled={!leaveToCancel?.id || cancelingId === leaveToCancel?.id}
            sx={{ borderRadius: 2, minWidth: 80 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Leave Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setViewDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Leave Details</Typography>
            <IconButton onClick={() => setViewDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLeave && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Chip
                  label={selectedLeave.type}
                  sx={{
                    bgcolor:
                      leaveTypes.find((t) => t.value === selectedLeave.type)
                        ?.color + "20",
                    color: leaveTypes.find(
                      (t) => t.value === selectedLeave.type
                    )?.color,
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    py: 2,
                    px: 1,
                  }}
                />
                <Chip
                  label={selectedLeave.status}
                  color={getStatusColor(selectedLeave.status)}
                  icon={getStatusIcon(selectedLeave.status)}
                  sx={{ textTransform: "capitalize", fontWeight: 600 }}
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    From Date
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedLeave.startDate}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    To Date
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedLeave.endDate}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Duration
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedLeave.days} day(s)
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Applied On
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedLeave.appliedOn}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Notes
                  </Typography>
                  <Typography variant="body1">
                    {selectedLeave.reason}
                  </Typography>
                </Grid>
                {(selectedLeave.status === "approved" || selectedLeave.status === "rejected") && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        {selectedLeave.status === "approved" ? "Approved By" : "Rejected By"}
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {selectedLeave.approvedBy?.fullName ||
                          selectedLeave.approvedBy?.name ||
                          selectedLeave.approvedBy ||
                          "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        {selectedLeave.status === "approved" ? "Approved On" : "Rejected On"}
                      </Typography>
                      <Typography variant="body1">
                        {formatDisplayDate(
                          selectedLeave.status === "approved"
                            ? selectedLeave.approvedOn
                            : selectedLeave.rejectedOn
                        )}
                      </Typography>
                    </Grid>
                  </>
                )}
                {selectedLeave.remarks && selectedLeave.status === "rejected" && (
                  <Grid item xs={12}>
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      <Typography variant="body2">
                        <strong>Rejection Reason:</strong> {selectedLeave.remarks}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StaffLeaves;
